import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { useLang } from "@/i18n";
import { api, fmtErr } from "@/api";
import { User, Briefcase } from "lucide-react";

export default function Register() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", password: "",
    role: params.get("role") === "dsa" ? "dsa" : "customer",
    referral_code: params.get("ref") || "",
  });
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/register", {
        ...form,
        referral_code: form.referral_code || null,
      });
      setDone(true);
    } catch (err) {
      setError(fmtErr(err));
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full border border-[#E5E2D9] bg-[#FDFCF8] px-4 py-3 text-sm outline-none focus:border-[#D4AF37] transition-colors duration-200";
  const labelCls = "block text-xs font-bold uppercase tracking-[0.15em] text-[#5C5C5C] mb-2";

  return (
    <div className="min-h-screen bg-[#F3F1EB] flex items-center justify-center px-6 py-16" data-testid="register-page">
      <div className="w-full max-w-lg">
        <Link to="/" className="flex justify-center mb-10" data-testid="register-home-link">
          <Logo size={44} />
        </Link>
        <div className="bg-white border border-[#E5E2D9] p-8 sm:p-10 shadow-[8px_8px_0_0_#E5E2D9]">
          {done ? (
            <div className="text-center py-6" data-testid="register-success">
              <div className="w-16 h-16 mx-auto bg-[#F7F0D8] flex items-center justify-center mb-6">
                <User size={28} className="text-[#B5952F]" />
              </div>
              <h1 className="font-display text-2xl font-bold text-[#1A1A1A]">Registration Submitted</h1>
              <p className="text-sm text-[#5C5C5C] mt-3 leading-relaxed">
                {form.role === "dsa"
                  ? "Your account is pending admin approval. You will be able to login once the Matrix Works team activates your account."
                  : "Your account is ready. Log in now and start your first investment — just share the payment screenshot and reference ID for verification."}
              </p>
              <Link
                to="/login"
                className="inline-block mt-8 bg-[#1A1A1A] text-[#D4AF37] px-8 py-3.5 text-sm font-bold tracking-wide hover:bg-[#D4AF37] hover:text-[#1A1A1A] transition-colors duration-300"
                data-testid="register-goto-login-button"
              >
                {t("nav_login")}
              </Link>
            </div>
          ) : (
            <>
              <h1 className="font-display text-3xl font-bold text-[#1A1A1A]">{t("register_title")}</h1>
              <p className="text-sm text-[#5C5C5C] mt-2 mb-8">{form.role === "dsa" ? t("register_sub") : t("register_sub_customer")}</p>
              {error && (
                <div className="mb-6 border border-[#E03131] bg-[#E03131]/5 text-[#E03131] text-sm font-semibold px-4 py-3" data-testid="register-error">
                  {error}
                </div>
              )}
              <div className="grid grid-cols-2 gap-3 mb-6" data-testid="register-role-selector">
                {[
                  { id: "customer", label: "Customer", icon: User },
                  { id: "dsa", label: "DSA Partner", icon: Briefcase },
                ].map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => setForm({ ...form, role: r.id })}
                    data-testid={`register-role-${r.id}`}
                    className={`flex items-center gap-3 border px-4 py-4 text-sm font-bold transition-colors duration-200 ${
                      form.role === r.id
                        ? "border-[#1A1A1A] bg-[#1A1A1A] text-[#D4AF37]"
                        : "border-[#E5E2D9] text-[#5C5C5C] hover:border-[#D4AF37]"
                    }`}
                  >
                    <r.icon size={18} /> {r.label}
                  </button>
                ))}
              </div>
              <form onSubmit={submit} className="space-y-5">
                <div>
                  <label className={labelCls}>Full Name</label>
                  <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} placeholder="Rajesh Patel" data-testid="register-name-input" />
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className={labelCls}>Email</label>
                    <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} placeholder="you@example.com" data-testid="register-email-input" />
                  </div>
                  <div>
                    <label className={labelCls}>Phone</label>
                    <input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} placeholder="+91 98765 43210" data-testid="register-phone-input" />
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Password</label>
                  <input type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} placeholder="Min. 6 characters" data-testid="register-password-input" />
                </div>
                {form.role === "customer" && (
                  <div>
                    <label className={labelCls}>DSA Referral Code (optional)</label>
                    <input value={form.referral_code} onChange={(e) => setForm({ ...form, referral_code: e.target.value })} className={inputCls} placeholder="e.g. MW123456" data-testid="register-referral-input" />
                  </div>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#D4AF37] text-[#1A1A1A] py-4 text-sm font-bold tracking-wide hover:bg-[#1A1A1A] hover:text-[#D4AF37] transition-colors duration-300 disabled:opacity-50"
                  data-testid="register-submit-button"
                >
                  {loading ? "..." : t("register_title")}
                </button>
              </form>
              <p className="text-sm text-[#5C5C5C] mt-8 text-center">
                Already registered?{" "}
                <Link to="/login" className="font-bold text-[#B5952F] hover:underline" data-testid="register-login-link">
                  {t("nav_login")}
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
